/**
 * LabelContentRenderer - 统一的标签内容渲染器
 *
 * 用于：
 * 1. LabelCreator的预览区
 * 2. LabelView的实际查看页面
 * 3. 确保预览和实际完全一致（所见即所得）
 */

import React from 'react';
import TextContent from './TextContent';
import ImageContent from './ImageContent';
import VideoContent from './VideoContent';
import AudioContent from './AudioContent';
import LinkContent from './LinkContent';
import AttachmentContent from './AttachmentContent';
import DividerContent from './DividerContent';
import ProductBasicContent from './ProductBasicContent';
import NutritionContent from './NutritionContent';
import UsageContent from './UsageContent';
import RelatedLinksContent from './RelatedLinksContent';
import InspectionContent from './InspectionContent';
import GDSTraceabilityContent from './GDSTraceabilityContent';
import './styles/label-content.css';

/**
 * 内容项加载占位符 - 使用固定高度避免布局跳动
 * 高度值经过调优，尽量接近实际内容高度
 */
function ContentItemSkeleton({ type }) {
  // 返回 [minHeight, maxHeight] 用于限制高度范围
  const getHeight = () => {
    switch (type) {
      case 'image': return '220px';      // 图片通常较高
      case 'video': return '200px';      // 视频播放器
      case 'product-basic': return '200px'; // 产品信息表格
      case 'nutrition': return '280px';  // 营养成分表较长
      case 'usage': return '120px';      // 食用方法
      case 'inspection':
      case 'certificate': return '80px'; // 检验报告按钮
      case 'related-links': return '100px'; // 相关链接
      case 'text': return '80px';        // 文本内容
      case 'divider': return '20px';     // 分割线
      case 'background': return '0px';
      default: return '100px';
    }
  };

  // background类型不显示骨架屏
  if (type === 'background') {
    return null;
  }

  const height = getHeight();

  return (
    <div
      className="content-item-skeleton"
      style={{
        height: height,
        minHeight: height,
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'skeleton-shimmer 1.5s infinite linear',
        borderRadius: '12px',
        margin: '0'
      }}
    />
  );
}

/**
 * 主渲染器组件
 * @param {Object} item - 内容项 { type, data }
 * @param {string} mode - 'preview' | 'view' (未来可能需要不同模式)
 * @param {string} templateType - 模板类型，如 'standard-food-label'
 * @param {boolean} priority - 是否高优先级加载（用于首屏内容）
 */
export default function LabelContentRenderer({ item, mode = 'view', templateType, priority = false }) {
  if (!item || !item.type) {
    return null;
  }

  const { type, data } = item;

  // 如果数据还未加载（懒加载中），显示骨架屏
  if (data === null) {
    return <ContentItemSkeleton type={type} />;
  }

  switch (type) {
    // 基础内容
    case 'text':
      return <TextContent data={data} mode={mode} templateType={templateType} />;

    case 'image':
      return <ImageContent data={data} mode={mode} templateType={templateType} priority={priority} />;
    case 'background':
      // 背景组件只用于应用到外层容器，不作为内容块渲染
      return null;

    case 'video':
      return <VideoContent data={data} mode={mode} templateType={templateType} priority={priority} />;
    
    case 'audio':
      return <AudioContent data={data} mode={mode} templateType={templateType} />;
    
    case 'link':
      return <LinkContent data={data} mode={mode} templateType={templateType} />;
    
    case 'attachment':
      return <AttachmentContent data={data} mode={mode} templateType={templateType} />;
    
    case 'divider':
      return <DividerContent data={data} />;
    
    // 业务模板
    case 'product-basic':
      return <ProductBasicContent data={data} mode={mode} templateType={templateType} />;
    
    case 'nutrition':
      return <NutritionContent data={data} mode={mode} templateType={templateType} />;
    
    case 'usage':
      return <UsageContent data={data} mode={mode} templateType={templateType} />;
    
    case 'related-links':
      return <RelatedLinksContent data={data} mode={mode} templateType={templateType} />;
    
    case 'inspection':
      return <InspectionContent data={data} mode={mode} templateType={templateType} />;
    
    case 'gds-traceability':
      return <GDSTraceabilityContent data={data} mode={mode} templateType={templateType} />;
    
    default:
      console.warn(`未知的内容类型: ${type}`);
      return null;
  }
}

// 导出所有子组件（供单独使用）
export {
  TextContent,
  ImageContent,
  VideoContent,
  AudioContent,
  LinkContent,
  AttachmentContent,
  DividerContent,
  ProductBasicContent,
  NutritionContent,
  UsageContent,
  RelatedLinksContent,
  InspectionContent,
  GDSTraceabilityContent
};
